/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { IHerbalIntroBlockConfig, IHerbalIntroBlockProps } from './herbal-intro-block.props.autogenerated';

export interface IHerbalIntroBlockState {
    intervalId: number;
    currentCount: number;
}
export interface IHerbalIntroBlockViewProps extends IHerbalIntroBlockProps<{}> {
    state: IHerbalIntroBlockState;
    text?: React.ReactNode;
    image?: React.ReactNode;
    className?: string;
    contentBlockContainer: IModuleProps;
    imageContainer: INodeProps;
    detailsContainer: INodeProps;
    contentBlockAnchorTag?: INodeProps;
    imageLink?: string;
    duration?: number;
}

/**
 * Site-builder configuration for the module.
 */
export interface IHerbalIntroBlockFullConfig extends IHerbalIntroBlockConfig {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- This field comes from SDK.
    msdyn365__moduleLayout?: string;
}

/**
 * ContentCard component.
 * @extends {React.PureComponent<IHerbalIntroBlockProps{}>}
 */
export class HerbalIntroBlock extends React.PureComponent<IHerbalIntroBlockProps<{}>, IHerbalIntroBlockState> {
    constructor(props: IHerbalIntroBlockProps<{}>, state: IHerbalIntroBlockState) {
        super(props);
        this.timer = this.timer.bind(this);
        this.state = {
            intervalId: 1,
            currentCount: 0
        };
    }

    public async componentDidMount(): Promise<void> {
        const {
            context: {
                request: { user }
            }
        } = this.props;
        if (!user.token) {
            this.setState({ currentCount: 1 });
            const duration = this.props.config?.duration;

            if (duration !== undefined) {
                var intervalId = window.setInterval(this.timer, duration * 1000);
                // store intervalId in the state so it can be accessed later:
                this.setState({ intervalId: intervalId });
            }
        } else {
            const videoBanner: HTMLMediaElement | null = document.querySelector('.msc-videoplayer__video');
            if (videoBanner) {
                videoBanner.removeAttribute('loop');
                setTimeout(() => {
                    videoBanner.pause();
                }, 100);
            }
            const videoBannerText: HTMLElement | null = document.querySelector('.ed-banner-video');
            if (videoBannerText) {
                videoBannerText.classList.add('hidden-intro');
            }
        }

        window.addEventListener('scroll', () => {
            const videoBannerText: HTMLElement | null = document.querySelector('.ed-banner-video');
            const scrollY = window.scrollY;
            if (videoBannerText) {
                if (scrollY >= 100) {
                    videoBannerText.classList.add('ed-banner-video--hide');
                } else {
                    videoBannerText.classList.remove('ed-banner-video--hide');
                }
            }
        });
    }

    public componentWillUnmount(): void {
        // use intervalId from the state to clear the interval
        clearInterval(this.state.intervalId);
    }

    private timer(el: any): void {
        var newCount = this.state.currentCount - 1;
        if (newCount >= 0) {
            this.setState({ currentCount: newCount });
        } else {
            clearInterval(this.state.intervalId);
        }
    }

    public render(): JSX.Element | null {
        // console.log(this.state);
        const { paragraph, image, className } = this.props.config;
        const imageProps = {
            gridSettings: this.props.context.request.gridSettings ?? {},
            imageSettings: image?.imageSettings
        };
        const contentBlockText = paragraph && <Msdyn365.RichTextComponent text={paragraph} className='ms-content-block__text' />;
        const contentBlockImage = (
            // @ts-ignore
            <Msdyn365.Image
                {...image}
                {...imageProps}
                requestContext={this.props.context.request}
                editProps={{
                    key: this.props.config.image ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image',
                    moduleId: this.props.id,
                    layout: (this.props.config as IHerbalIntroBlockFullConfig).msdyn365__moduleLayout
                }}
                shouldSkipToMainImage
            />
        );

        if (!contentBlockText) {
            this.props.context.telemetry.error('Content block content is empty, module wont render.');
            return null;
        }
        const contentBlockviewProps = {
            ...this.props,
            text: contentBlockText,
            image: contentBlockImage,
            moduleClass: this.props.config.className,
            contentBlockContainer: {
                moduleProps: this.props,
                className: this.state.currentCount ? classnames('ms-content-block ms-intro-page', className) : 'ms-content-block d-none'
            },
            imageContainer: { className: 'ms-background-container' },
            detailsContainer: { className: 'ms-content-container' },
            contentBlockAnchorTag: {
                tag: 'a',
                className: 'ms-content-block__link',
                role: 'link'
            },
            imageLink: this._getImageLink()
        };
        return this.props.renderView(contentBlockviewProps) as React.ReactElement;
    }

    /**
     * Get image link.
     */
    private _getImageLink(): string | null {
        const { imageLink } = this.props.config;
        if (imageLink?.destinationUrl) {
            return imageLink.destinationUrl;
        }
        return null;
    }
}

export default HerbalIntroBlock;
